import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
    Grid,
    Box,
    Card,
    Typography,
    Checkbox,
    IconButton,
    Snackbar,
    Alert,
    CircularProgress,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftInput from "components/SoftInput";
import SoftButton from "components/SoftButton";
import logo from "assets/images/logo.png";
import { useTranslation } from "react-i18next";
import TermsAndConditionsDialog from "./TermsAndConditionsDialog";
import { register } from "../../../axios-client";
import LanguageSwitcher from "../../../reusableComponents/LanguageSwitcher/LanguageSwitcher";
import {useTheme} from "@mui/material/styles";
import {setLayout} from "../../../store/actions/app-actions";
import {useSoftUIController} from "../../../context";

function SignUp() {
    const theme = useTheme();

    const [agreement, setAgreement] = useState(true);
    const [formData, setFormData] = useState({
        name: "",
        email: "",
        password: "",
        university: "",
        department: [],
        agreement: "",
        isFreeAgent: true
    });
    const [showPassword, setShowPassword] = useState(false);
    const [errors, setErrors] = useState({});
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "" });
    const [isActivating, setIsActivating] = useState(false);
    const [openTermsDialog, setOpenTermsDialog] = useState(false);
    const [controller, dispatch] = useSoftUIController();


    const navigate = useNavigate();

    const [animateImage, setAnimateImage] = useState(false);
    const [animateTitle, setAnimateTitle] = useState(false);
    const [animateForm, setAnimateForm] = useState(false);
    const [animateLogo, setAnimateLogo] = useState(false);

    useEffect(() => {
        setTimeout(() => setAnimateLogo(true), 200);
        setTimeout(() => setAnimateImage(true), 400);
        setTimeout(() => setAnimateTitle(true), 600);
        setTimeout(() => setAnimateForm(true), 800);

        setLayout(dispatch, "page");
    }, []);

    const {i18n} = useTranslation();
    const { t } = useTranslation(['signUp']);
    const dir = i18n.dir();
    const currentLang = i18n.language;

    const signInUrl = `/${currentLang}/authentication/sign-in`;

    const features = t('panel.features', { returnObjects: true });

    const handleTermsOpen = () => {
        setOpenTermsDialog(true);
    };

    const handleTermsClose = () => {
        setOpenTermsDialog(false);
    };


    const handleSetAgreement = () => {
        setAgreement(!agreement);
    };

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const togglePasswordVisibility = () => {
        setShowPassword(!showPassword);
    };


    const handleSubmit =  (e) => {
        e.preventDefault();
        if (validateForm()) {
            setIsActivating(true);
            const updatedFormData = {
                ...formData,
                language: currentLang,
            };

            register(updatedFormData)
                .then((response) => {
                    setSnackbar({
                        open: true,
                        message: t("signUp.registrationSuccess"),
                        severity: "success",
                    });
                    return new Promise(resolve => setTimeout(resolve, 1000));
                })
                .then(() => {
                    navigate(`/${currentLang}/authentication/confirmation`, { state:  "free-agent"  });
                })
                .catch((error) => {
                    return new Promise(resolve => setTimeout(resolve, 2000))
                        .then(() => handleRegistrationError(error));
                })
                .finally(() => {
                    setIsActivating(false);
                });
        }
    };

    const handleRegistrationError = (error) => {
        if (error.response && error.response.status === 400 && error.response.data.message === "Email already exists") {
            setSnackbar({
                open: true,
                message: t("signUp.errors.emailRegistered"),
                severity: "error",
            });
        } else {
            setSnackbar({
                    open: true,
                    message: t("signUp.errors.registration"),
                    severity: "error",
                }
            );}
        setIsActivating(false);
    };


    const validateForm = () => {
        const newErrors = {};
        const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (formData.email && !emailPattern.test(formData.email)) {
            newErrors.email = t("signUp.errors.validEmail");
        }
        if (formData.password && formData.password.length < 8) {
            newErrors.password = t("signUp.errors.passwordLength");
        } else if (formData.password && !/[!@#$%^&*]/.test(formData.password)) {
            newErrors.password = t("signUp.errors.passwordCharacters");
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSnackbarClose = () => {
        setSnackbar({ open: false, message: "", severity: "" });
    };



    return (
        <>
            <style>
                {`
          @keyframes fadeSlideIn {
            0% { opacity: 0; transform: translateY(20px); }
            100% { opacity: 1; transform: translateY(0); }
          }
         @keyframes scan {
            0% { top: 0;} 
            50% { top: 100%;}
            100% { top: 0;}
            }
        `}
            </style>

            <Grid container sx={{ minHeight: "100vh", overflow: "hidden" }} dir={dir}>
                <Grid
                    item
                    xs={12}
                    md={6}
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center",
                        backgroundColor: "#ffffff",
                        position: "relative",
                        p: 5,
                    }}
                >
                    <Box
                        sx={{
                            position: "absolute",
                            top: theme.spacing(2),
                            left: theme.spacing(2),
                            right: theme.spacing(2),
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            zIndex: 10,
                        }}
                    >
                        <Box
                            sx={{
                                opacity: animateLogo ? 1 : 0,
                                transform: animateLogo ? "translateY(0)" : "translateY(-10px)",
                                transition: "all 0.8s ease",
                            }}

                        >
                            <img src={logo} alt="YaiGlobal Logo" style={{ height: 48 }} />
                        </Box>

                        <LanguageSwitcher />
                    </Box>

                    <Box>
                        {/* Title */}
                        <Typography
                            variant="h4"
                            fontWeight="bold"
                            sx={{
                                background: "linear-gradient(to right, #d946ef, #ec4899)",
                                WebkitBackgroundClip: "text",
                                WebkitTextFillColor: "transparent",
                                opacity: animateTitle ? 1 : 0,
                                transform: animateTitle ? "translateX(0)" : "translateX(-10px)",
                                transition: "all 0.7s ease",
                            }}
                        >
                            {t("signUp.createAccount")}
                        </Typography>
                        <Typography
                            variant="body2"
                            color="textSecondary"
                            sx={{
                                mb: 3,
                                opacity: animateTitle ? 1 : 0,
                                transition: "opacity 0.8s ease 0.2s",
                            }}
                        >
                            {t("signUp.description")}
                        </Typography>

                        <Card
                            elevation={4}
                            sx={{
                                borderRadius: 2,
                                p: 3,
                                opacity: animateForm ? 1 : 0,
                                transform: animateForm ? "translateY(0)" : "translateY(10px)",
                                transition: "all 0.7s ease",
                            }}
                        >
                            <form onSubmit={handleSubmit}>
                                <SoftBox mb={2}>
                                    <SoftInput
                                        type="text"
                                        placeholder={t("signUp.name")}
                                        name="name"
                                        value={formData.name}
                                        onChange={handleChange}
                                        required
                                    />
                                </SoftBox>

                                <SoftBox mb={2}>
                                    <SoftInput
                                        type="email"
                                        placeholder={t("signUp.email")}
                                        name="email"
                                        value={formData.email}
                                        onChange={handleChange}
                                        required
                                    />
                                </SoftBox>

                                <SoftBox mb={2} position="relative">
                                    <SoftInput
                                        type={showPassword ? "text" : "password"}
                                        placeholder={t("signUp.password")}
                                        name="password"
                                        value={formData.password}
                                        onChange={handleChange}
                                        required
                                    />
                                    <IconButton
                                        onClick={togglePasswordVisibility}
                                        sx={{ position: "absolute",  ...(i18n.language === "ar" ? {left:10} : {right:10}), top: "50%", transform: "translateY(-50%)",
                                            marginRight: currentLang == 'ar' ? "205px" : "0px"
                                        }}
                                    >
                                        {showPassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                    {errors.password && (
                                        <SoftTypography color="error" sx={{fontSize: "0.75rem"}}>{errors.password}</SoftTypography>
                                    )}
                                </SoftBox>

                                <SoftBox display="flex" alignItems="center" mt={1}>
                                    <Checkbox checked={agreement} onChange={handleSetAgreement}
                                        required={true}
                                    />
                                    <Typography variant="caption">
                                        {t("signUp.agreement")}{" "}
                                        <span onClick={handleTermsOpen} style={{ color: "#cb0c9f", cursor: "pointer" }}>
                                          {t("signUp.terms")}
                                        </span>
                                    </Typography>
                                </SoftBox>
                                {errors.agreement && <SoftTypography color="error">{errors.agreement}</SoftTypography>}

                                <SoftBox mt={3}>
                                    <SoftButton variant="gradient" color="primary" fullWidth type="submit">
                                        {isActivating ? t("signUp.pleaseWait") : t("signUp.signUp")}
                                        {isActivating && <CircularProgress size={20} sx={{ ml: 1, color: "white" }} />}
                                    </SoftButton>
                                </SoftBox>

                                <SoftBox mt={3} textAlign="center">
                                    <Typography variant="caption">
                                        {t("signUp.alreadyHasAccount")}{" "}
                                        <Link to={signInUrl} style={{ color: "#cb0c9f", fontWeight: 500 }}>
                                            {t("signUp.signIn")}
                                        </Link>
                                    </Typography>
                                </SoftBox>
                            </form>
                        </Card>
                    </Box>
                </Grid>

                {/* Right Side Decorative Panel */}
                <Grid item md={6} sx={{ display: { xs: "none", md: "block" }, position: "relative", overflow: "hidden" }}>
                    <Box
                        sx={{
                            width: "100%",
                            height: "100%",
                            background: "linear-gradient(135deg, rgba(217,70,239,0.9), rgba(126,34,206,0.9))",
                            position: "relative",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            opacity: animateImage ? 0.8 : 0,
                            transition: "opacity 1s ease",
                        }}
                    >
                        <Box
                            sx={{
                                position: "relative",
                                zIndex: 1,
                                p: 4,
                                maxWidth: 500,
                                backgroundColor: "rgba(255,255,255,0.1)",
                                borderRadius: 4,
                                border: "1px solid rgba(255,255,255,0.2)",
                                animation: animateImage ? "fadeSlideIn 1s ease forwards" : "none",
                            }}
                        >
                            <Typography variant="h6" fontWeight="bold" sx={{ color: "#fff", mb: 2 }}>
                                {t("panel.brand")}
                            </Typography>
                            <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ color: "#fff" }}>
                                {t("panel.headline")}
                            </Typography>
                            <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.8)", mb: 3 }}>
                                {t("panel.body")}
                            </Typography>
                            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                                {features.map((label) => (
                                    <Box
                                        key={label}
                                        sx={{
                                            px: 2,
                                            py: 0.5,
                                            borderRadius: 50,
                                            backgroundColor: "rgba(255,255,255,0.2)",
                                            fontSize: "0.875rem",
                                            color: "#fff",
                                        }}
                                    >
                                        {label}
                                    </Box>
                                ))}
                            </Box>
                            <Box
                                sx={{
                                    position: 'absolute',
                                    left: 0,
                                    width: '100%',
                                    height: '1.5px',
                                    backgroundColor: 'rgba(250, 204, 21, 0.7)',
                                    boxShadow: '0 0 8px 2px rgba(250, 204, 21, 0.5)',
                                    animation: 'scan 20s ease-in-out infinite',
                                }}
                            />
                        </Box>
                    </Box>

                    <Box
                        component="svg"
                        xmlns="http://www.w3.org/2000/svg"
                        sx={{ position: "absolute", inset: 0, width: "100%", height: "100%", opacity: 0.2 }}
                    >
                        <defs>
                            <pattern id="circuit-pattern" x="0" y="0" width="100" height="100" patternUnits="userSpaceOnUse">
                                <path
                                    d="M10,10 L40,10 L40,40 L70,40 L70,70 L40,70 L40,40 M60,10 L90,10 L90,40 M10,30 L10,60 L30,60 M10,80 L40,80 L40,90 L70,90 M60,30 L60,60 L90,60 L90,90"
                                    fill="none"
                                    stroke="white"
                                    strokeWidth="1"
                                />
                            </pattern>
                        </defs>
                        <rect x="0" y="0" width="100%" height="100%" fill="url(#circuit-pattern)" />
                    </Box>
                </Grid>
            </Grid>

            <TermsAndConditionsDialog open={openTermsDialog} close={handleTermsClose} />

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={handleSnackbarClose}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert severity={snackbar.severity} onClose={handleSnackbarClose}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </>
    );
}

export default SignUp;